import {useEffect} from "react";
import {useTranslation} from "react-i18next";
import {registerPlugin} from "@capacitor/core";
import {useAppUser} from "contexts/AppUserContext";
import {useNavigations} from "contexts/NavigationsContext";
import {isAndroid} from "FirebaseUtils";
import {importGpxFileToActivityDb} from "services/GPXService";
import {importVkxFileToActivityDb} from "services/VKXService";
import {createActivityFromFitFile} from "services/ActivityService";
import {finalizeImportedActivity} from "services/ImportBridgeService";
import ActivityAnalyticsService from "services/ActivityAnalyticsService";

interface ImportBridgePluginInterface {
  addListener: (eventName: string, listenerFunc: (data: any) => void) => Promise<{remove: () => void}>;
  notifyImportResult: (options: {success: boolean; message: string}) => Promise<void>;
}

const ImportBridgePlugin = registerPlugin<ImportBridgePluginInterface>("ImportBridgePlugin");

function base64ToArrayBuffer(base64: string): ArrayBuffer {
  const binaryString = atob(base64);
  const bytes = new Uint8Array(binaryString.length);
  for (let i = 0; i < binaryString.length; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  return bytes.buffer;
}

/**
 * Android's native file picker has no "user activation" requirement to satisfy, unlike a WebView
 * <input type="file">.click() triggered from outside a real DOM click (see ImportBridgePlugin.kt
 * for why the native menu can't just drive ImportGPXButton/ImportFITButton/ImportVKXButton's
 * existing trigger-ref pattern). Native picks the file and hands the raw content here to reuse
 * those buttons' exact parsers and write path instead of duplicating them a fourth time. This
 * runs with the WebView invisible the whole time — notifyImportResult reports back to native,
 * which shows a Toast and refreshes Diary itself; nothing here ever needs to be seen.
 */
function ImportBridgeListener() {
  const {t} = useTranslation();
  const {appUser} = useAppUser();
  const {addNavigationsToState} = useNavigations() as any;

  useEffect(() => {
    if (!isAndroid) return undefined;

    let listenerHandle: {remove: () => void} | null = null;
    let isMounted = true;

    (async () => {
      const handle = await ImportBridgePlugin.addListener("fileSelected", async (data) => {
        const {fileType, content, isBase64} = data || {};
        if (!appUser?.uid || !fileType || content === undefined) return;

        try {
          if (fileType === "gpx") {
            const {activityDb, pointsDb} = await importGpxFileToActivityDb(content, appUser.uid);
            await finalizeImportedActivity(activityDb, pointsDb, appUser.uid, addNavigationsToState);
            ActivityAnalyticsService.trackGpxImported();
            await ImportBridgePlugin.notifyImportResult({success: true, message: t("common.gpxImportSuccess")});
          } else if (fileType === "fit") {
            const buffer = isBase64 ? base64ToArrayBuffer(content) : content;
            const activity = await createActivityFromFitFile(buffer, appUser.uid);
            if (!activity) {
              await ImportBridgePlugin.notifyImportResult({
                success: false,
                message: t("common.fitSportNotSupported")
              });
              return;
            }
            const {activityDb, pointsDb} = activity.convertActivityToActivityDbAndPointsDb(appUser.uid);
            await finalizeImportedActivity(activityDb, pointsDb, appUser.uid, addNavigationsToState);
            ActivityAnalyticsService.trackFitImported();
            await ImportBridgePlugin.notifyImportResult({success: true, message: t("common.fitImportSuccess")});
          } else if (fileType === "vkx") {
            const buffer = isBase64 ? base64ToArrayBuffer(content) : content;
            const {activityDb, pointsDb} = await importVkxFileToActivityDb(buffer, appUser.uid);
            await finalizeImportedActivity(activityDb, pointsDb, appUser.uid, addNavigationsToState);
            ActivityAnalyticsService.trackVkxImported();
            await ImportBridgePlugin.notifyImportResult({success: true, message: t("common.vkxImportSuccess")});
          }
        } catch (error) {
          console.error(`Error processing ${fileType} file from native picker:`, error);
          const errorKey =
            fileType === "gpx" ? "gpxProcessError" : fileType === "fit" ? "fitProcessError" : "vkxProcessError";
          await ImportBridgePlugin.notifyImportResult({success: false, message: t(`common.${errorKey}`)});
        }
      });
      if (isMounted) {
        listenerHandle = handle;
      } else {
        handle.remove();
      }
    })();

    return () => {
      isMounted = false;
      listenerHandle?.remove();
    };
  }, [appUser?.uid, addNavigationsToState, t]);

  return null;
}

export default ImportBridgeListener;
